// Auto-rollback watch-state colors + labels for the v2 UI (DEVOPS-1629).
//
// Mirrors statusTokens.ts conventions (Notion-sticker hues, quiet tag pills).
// The 7 canonical states come from spec §18.2; anything unrecognized (e.g. a
// future state, or the stray `failed` observed in real data) folds to `unknown`
// so the pill always renders something sensible.

import { sticker } from './tokens';

export type CanonicalWatchState =
  | 'not_watched'
  | 'pending'
  | 'watching'
  | 'rolling_back'
  | 'rolled_back'
  | 'rollback_failed'
  | 'no_rollback'
  | 'unknown';

const KNOWN: Record<string, CanonicalWatchState> = {
  not_watched: 'not_watched',
  pending: 'pending',
  watching: 'watching',
  rolling_back: 'rolling_back',
  rolled_back: 'rolled_back',
  rollback_failed: 'rollback_failed',
  no_rollback: 'no_rollback',
};

export const normalizeWatchState = (state?: string): CanonicalWatchState =>
  (state && KNOWN[state.toLowerCase()]) || 'unknown';

/** Whether a watch state is worth surfacing as a pill at all. */
export const hasWatchState = (state?: string): boolean =>
  !!state && normalizeWatchState(state) !== 'unknown';

// User-facing labels. "Monitor" reads better than the internal "watch"
// vocabulary on a customer dashboard (the data field stays `watch_state`).
export const watchLabels: Record<CanonicalWatchState, string> = {
  not_watched: 'Not monitored',
  pending: 'Monitor pending',
  watching: 'Monitoring',
  rolling_back: 'Rolling back',
  rolled_back: 'Rolled back',
  rollback_failed: 'Rollback failed',
  no_rollback: 'Cleared',
  unknown: 'Monitor',
};

export const watchHues: Record<CanonicalWatchState, string> = {
  not_watched: '#9b9a97',     // muted grey — gate decided not to watch
  pending: sticker.orange,    // queued
  watching: '#0b76d1',        // active deployment blue (matches inprogress)
  rolling_back: '#dd5b00',    // hot orange — action in flight
  rolled_back: '#8155b8',     // purple — a rollback happened
  rollback_failed: '#d93a3a', // red — needs attention
  no_rollback: sticker.green, // window elapsed clean
  unknown: '#9b9a97',
};

export const getWatchHue = (state?: string): string =>
  watchHues[normalizeWatchState(state)];

export const getWatchLabel = (state?: string): string =>
  watchLabels[normalizeWatchState(state)];

/** Terminal states whose end_reason is worth revealing on hover. */
export const isTerminalWatchState = (state?: string): boolean => {
  switch (normalizeWatchState(state)) {
    case 'not_watched':
    case 'rolled_back':
    case 'rollback_failed':
    case 'no_rollback':
      return true;
    default:
      return false;
  }
};
